/**
 * Navigation paths for the application.
 * All route paths should be defined here to maintain consistency
 * and make it easier to refactor routes in the future.
 */
import {
  CONNECTION_BRIDGE_PARAM,
  CONNECTION_ENTITY_PARAM,
  CONNECTION_HELP_PARAM,
  CONNECTION_PLUGIN_PARAM,
  CONNECTION_RETURN_TO_PARAM,
  NEW_CONNECTION_SEGMENT,
  type ConnectionHelpTopic
} from './features/integrations/constants/connection-routes';
import {
  CONNECTOR_FIELD_PARAM,
  type ConnectorTab
} from './features/integrations/constants/connector-tabs';
import { SetupParam, type SetupStepId } from './features/integrations/constants/setup-wizard';
import {
  TemplateCopyParam,
  TemplateFromConnectionParam
} from './features/master-account-integration-templates/constants/template-routes';
import type { LifecycleStepParam } from './features/admin/components/system-settings/automation/types';
import type {
  CodeListSlug,
  SettingsSectionSlug
} from './components/settings-shell/settings-sections';

/** Every master-account-board path lives under this prefix. */
export const MASTER_ACCOUNT_BOARD_PATH_PREFIX = '/master-account';

/** Whether a pathname belongs to the master account board. */
export function isMasterAccountBoardPath(pathname: string): boolean {
  return (
    pathname === MASTER_ACCOUNT_BOARD_PATH_PREFIX ||
    pathname.startsWith(`${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/`)
  );
}

/** The anchor of the bank accounts section on a party's detail page. */
export const PARTNER_BANK_ACCOUNTS_ANCHOR = 'bank-accounts';

/** A route parameter such as `:id`: the helpers below also give the route patterns. */
const ROUTE_PARAM = /^:\w+$/;

/** An id as exactly one path segment, so it cannot name another route or add a parameter. */
function idSegment(id: string): string {
  if (ROUTE_PARAM.test(id)) return id;
  // A URL resolves `..` and `%2e%2e` alike, so a dot segment is encoded twice: it names nothing.
  if (id === '.' || id === '..') return '%252E'.repeat(id.length);
  return encodeURIComponent(id);
}

export const navigation = {
  /**
   * Home & Dashboard page path
   */
  dashboard: () => '/',

  /**
   * Full-page Tappeer AI chat, without the home page's onboarding redirect
   */
  tappeerAi: () => '/tappeer-ai',

  /**
   * Master account board: the client list, the board's home. Period, filters and position live in its
   * query string.
   */
  accountRelations: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/clients`,

  /**
   * Master account board: one client's detail
   */
  accountRelation: (clientId: string) => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/clients/${clientId}`,

  /**
   * Master account board: cross-client queue of waiting documents. The position in the queue lives in
   * its query string, so the way back continues where the work stopped.
   */
  masterAccountQueue: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/queue`,

  /**
   * Master account board: Nastavení kanceláře, whose sections live under it; opens the first one
   */
  masterAccountSettings: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings`,

  /**
   * Master account board: the master account's identity (settings section)
   */
  masterAccountOfficeSettings: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/office`,

  /**
   * Master account board: the master account's deletion (settings section)
   */
  masterAccountManage: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/manage`,

  /**
   * Master account board: the master account's people (settings section)
   */
  masterAccountPeople: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/people`,

  /**
   * Master account board: the master account's plan, billing and invoices (settings section)
   */
  masterAccountBilling: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/billing`,

  /**
   * Master account board: the master account's credit statement (settings section)
   */
  masterAccountCreditTransactions: () =>
    `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/billing/credit-transactions`,

  /**
   * Master account board: the master account's own bridges (settings section)
   */
  masterAccountBridges: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/bridges`,

  /**
   * Master account board: registering a bridge of the master account
   */
  masterAccountBridgeNew: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/bridges/new`,

  /**
   * Master account board: one bridge of the master account
   */
  masterAccountBridge: (id: string) => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/bridges/${id}`,

  /**
   * Master account board: the office's integration templates (settings section)
   */
  masterAccountIntegrationTemplates: () =>
    `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/integration-templates`,

  /**
   * Master account board: a new integration template of the office
   * @param params - A client's installation to save as the template, its name and per-client keys
   */
  masterAccountIntegrationTemplateNew: (params?: {
    fromClient: string;
    fromConnection: string;
    name: string;
    perClientKeys: readonly string[];
  }) => {
    const base = `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/integration-templates/new`;
    if (!params) return base;
    const query = new URLSearchParams();
    query.set(TemplateFromConnectionParam.CLIENT, params.fromClient);
    query.set(TemplateFromConnectionParam.CONNECTION, params.fromConnection);
    query.set(TemplateFromConnectionParam.NAME, params.name);
    for (const key of params.perClientKeys) {
      query.append(TemplateFromConnectionParam.PER_CLIENT_KEY, key);
    }
    return `${base}?${query.toString()}`;
  },

  /**
   * Master account board: one integration template of the office
   */
  masterAccountIntegrationTemplate: (id: string) =>
    `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/integration-templates/${id}`,

  /**
   * Master account board: applying one integration template of the office to its clients
   */
  masterAccountIntegrationTemplateApply: (id: string) =>
    `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/integration-templates/${id}/apply`,

  /**
   * Master account board: one client's installation copied to other clients, no template kept
   * @param params - The client, its installation, the installation's plugin and name, and whether
   *   it is on the client's own Bridge
   */
  masterAccountIntegrationTemplateCopy: (params?: {
    client: string;
    connection: string;
    plugin: string;
    installation: string;
    ownBridge?: boolean;
  }) => {
    const base = `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/settings/integration-templates/copy`;
    if (!params) return base;
    const query = new URLSearchParams();
    query.set(TemplateCopyParam.CLIENT, params.client);
    query.set(TemplateCopyParam.CONNECTION, params.connection);
    query.set(TemplateCopyParam.PLUGIN, params.plugin);
    query.set(TemplateCopyParam.INSTALLATION, params.installation);
    if (params.ownBridge) query.set(TemplateCopyParam.OWN_BRIDGE, '1');
    return `${base}?${query.toString()}`;
  },

  /**
   * Legacy board people path — redirects to `masterAccountPeople`
   */
  legacyMasterAccountPeople: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/people`,

  /**
   * Legacy board billing path and its sub-paths — redirect under `masterAccountBilling`
   */
  legacyMasterAccountBilling: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/billing`,

  /**
   * Legacy board bridges path and its sub-paths — redirect under `masterAccountBridges`
   */
  legacyMasterAccountBridges: () => `${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/bridges`,

  /**
   * Legacy master account board path — redirects to `accountRelations`
   */
  legacyMasterAccountBoard: () => '/board',

  /**
   * Legacy master account queue path — redirects to `masterAccountQueue`
   */
  legacyMasterAccountQueue: () => '/board/queue',

  /**
   * Sign in page path (passwordless — default)
   * @param params - Optional query parameters (e.g. `{ invited: 'true' }`)
   */
  signIn: (params?: Record<string, string>) => {
    const base = '/sign-in';
    if (params && Object.keys(params).length > 0) {
      return `${base}?${new URLSearchParams(params).toString()}`;
    }
    return base;
  },

  /**
   * Password sign in page path (hidden, for internal use)
   */
  passwordSignIn: () => '/sign-in-password',

  /**
   * Sign up page path (passwordless — default)
   */
  signUp: () => '/sign-up',

  /**
   * Magic link sent / "check your email" page
   */
  magicLinkSent: () => '/auth/magic-link-sent',

  /**
   * Magic link callback page (token verification)
   */
  magicLinkCallback: () => '/auth/magic-link',

  /**
   * Forgot password page path
   */
  forgotPassword: () => '/forgot-password',

  /**
   * Verify email page path
   */
  verifyEmail: () => '/auth/verify-email',

  /**
   * Email verification pending page path (shown after registration)
   */
  verificationPending: () => '/auth/verification-pending',

  /**
   * Reset password page path
   */
  resetPassword: () => '/auth/reset-password',

  /**
   * Accept invitation page path (for invited users)
   */
  acceptInvitation: () => '/auth/accept-invitation',

  /**
   * Impersonation callback page — redeems one-time token received from AMS via URL.
   * Public route — no authentication required.
   */
  impersonateCallback: () => '/auth/impersonate',

  /**
   * Unsubscribe from Tappeer's e-mails by a signed link (`?token=`) — public, for every auth state.
   */
  unsubscribe: () => '/unsubscribe',

  /**
   * Documents page path
   */
  documents: () => '/documents',

  /**
   * Document detail page path
   */
  documentDetail: (id?: string) => (id ? `/documents/${id}` : '/documents'),

  /**
   * Open questions between the master account and its client, across the account's documents
   */
  openQuestions: () => '/questions',

  /**
   * Invoices list page path
   */
  /**
   * Procurement section — the work queue is its front door, the per-type lists sit under it.
   */
  procurement: () => '/procurement',

  /** Requisitions list */
  requisitions: () => '/procurement/requisitions',

  /** Requisition detail */
  requisitionDetail: (id?: string) =>
    id ? `/procurement/requisitions/${id}` : '/procurement/requisitions',

  /** Purchase orders list */
  purchaseOrders: () => '/procurement/orders',

  /** Purchase order detail */
  purchaseOrderDetail: (id?: string) => (id ? `/procurement/orders/${id}` : '/procurement/orders'),

  /** Goods receipts list */
  goodsReceipts: () => '/procurement/receipts',

  /** Goods receipt detail */
  goodsReceiptDetail: (id?: string) =>
    id ? `/procurement/receipts/${id}` : '/procurement/receipts',

  invoices: () => '/invoices',

  /** Contracts list; outside `/procurement` because it follows its own flag. */
  contracts: () => '/contracts',

  /** Contract detail */
  contractDetail: (id: string) => `/contracts/${id}`,

  /**
   * Invoice detail page path
   */
  invoiceDetail: (id?: string) => (id ? `/invoices/${id}` : '/invoices'),

  /**
   * Bank movements: statement lines and ERP bank, cash and internal documents, read only
   */
  bankMovements: () => '/bank-movements',

  /** Payments: redirects to the payables */
  payments: () => '/payments',

  /** Faktury k úhradě: received invoices still to pay, and whether each can be paid now */
  payables: () => '/payments/payables',

  /** Platební dávky: the payment runs */
  paymentRuns: () => '/payments/runs',

  /** One payment run */
  paymentRunDetail: (id: string) => `/payments/runs/${id}`,

  /**
   * Uploaded bank statement files and how reading them went (a tab of the bank movements)
   */
  bankStatementImports: () => '/bank-movements/statements',

  /**
   * Statement lines waiting to be matched with a payment (a tab of the bank movements)
   */
  bankMovementQueue: () => '/bank-movements/queue',

  /**
   * Invoice scan editor page path (review/edit extracted fields).
   * Available only for invoices in editable approval statuses.
   */
  invoiceScanEditor: (id?: string) => (id ? `/invoices/${id}/scan-editor` : '/invoices'),

  /**
   * Receipt detail page path
   */
  receiptDetail: (id: string) => `/receipts/${id}`,

  /**
   * Persistent inbox of the current user (questions about the client's documents)
   */
  notifications: () => '/notifications',

  /**
   * My account/profile page path
   */
  myAccount: () => '/my-account',

  /**
   * System settings: the overview, or one section
   * @param section - Slug of the section (e.g. `'users'`); omit for the overview
   * @param params - `step` opens one automation step (`purchaseOrder.closure`)
   */
  settings: (section?: SettingsSectionSlug, params?: { step?: LifecycleStepParam }) => {
    if (!section) return '/settings';
    const query = params?.step ? `?${new URLSearchParams({ step: params.step }).toString()}` : '';
    return `/settings/${section}${query}`;
  },

  /**
   * Old System settings path — redirects to `settings`
   */
  legacySystemSettings: () => '/system-settings',

  /**
   * Code lists: one list in System settings
   * @param list - Slug of the list (e.g. `'cost-centers'`); omit for the first one
   */
  codeLists: (list: CodeListSlug = 'org-structure') => `/settings/code-lists/${list}`,

  /**
   * Code lists: their menu on its own (a phone's list); a desktop opens the first list
   */
  codeListsMenu: () => '/settings/code-lists',

  /**
   * Old Code lists page path — redirects to `codeLists`
   */
  legacyCodeLists: () => '/code-lists',

  /**
   * Integrations overview page path
   */
  integrations: () => '/settings/integrations',

  /**
   * Account-wide sync monitor page path
   */
  integrationMonitor: () => '/settings/integrations/monitor',

  /**
   * Connector detail page path
   * @param tab - Optional tab to open; `settings` is the default and omits `?tab`
   * @param field - Optional plugin setting to focus on the settings tab
   */
  integrationConnector: (id: string, tab?: ConnectorTab, field?: string) => {
    const query = new URLSearchParams();
    if (tab && tab !== 'settings') query.set('tab', tab);
    if (field) query.set(CONNECTOR_FIELD_PARAM, field);
    const search = query.toString();
    const base = `/settings/integrations/connectors/${idSegment(id)}`;
    return search ? `${base}?${search}` : base;
  },

  /**
   * Target connections page path
   */
  integrationConnections: () => '/settings/integrations/connections',

  /**
   * New target-system installation page path
   * @param params - The plugin it is created for; optionally the entity it must serve, the
   *   bridge and the page to return to
   */
  integrationConnectionNew: (params: {
    plugin: string;
    entity?: string;
    bridge?: string;
    returnTo?: string;
  }) => {
    const query = new URLSearchParams();
    query.set(CONNECTION_PLUGIN_PARAM, params.plugin);
    if (params.entity) query.set(CONNECTION_ENTITY_PARAM, params.entity);
    if (params.bridge) query.set(CONNECTION_BRIDGE_PARAM, params.bridge);
    if (params.returnTo) query.set(CONNECTION_RETURN_TO_PARAM, params.returnTo);
    return `/settings/integrations/connections/${NEW_CONNECTION_SEGMENT}?${query.toString()}`;
  },

  /**
   * Target-system installation page path
   * @param params - Optional setting to focus, help block to open and page to return to
   */
  integrationConnection: (
    id: string,
    params?: { field?: string; help?: ConnectionHelpTopic; returnTo?: string }
  ) => {
    const query = new URLSearchParams();
    if (params?.field) query.set(CONNECTOR_FIELD_PARAM, params.field);
    if (params?.help) query.set(CONNECTION_HELP_PARAM, params.help);
    if (params?.returnTo) query.set(CONNECTION_RETURN_TO_PARAM, params.returnTo);
    const search = query.toString();
    const base = `/settings/integrations/connections/${idSegment(id)}`;
    return search ? `${base}?${search}` : base;
  },

  /**
   * Integration setup wizard path
   * @param params - Optional system (catalogue reference), installation and step to show
   */
  integrationSetup: (params?: { plugin?: string; connection?: string; step?: SetupStepId }) => {
    const query = new URLSearchParams();
    if (params?.plugin) query.set(SetupParam.PLUGIN, params.plugin);
    if (params?.connection) query.set(SetupParam.CONNECTION, params.connection);
    if (params?.step) query.set(SetupParam.STEP, params.step);
    const search = query.toString();
    return search ? `/settings/integrations/setup?${search}` : '/settings/integrations/setup';
  },

  /**
   * Bridges page path
   */
  integrationBridges: () => '/settings/integrations/bridges',

  /**
   * Bridge registration page path
   */
  integrationBridgeNew: () => '/settings/integrations/bridges/new',

  /**
   * Bridge detail page path
   */
  integrationBridge: (id: string) => `/settings/integrations/bridges/${id}`,

  /**
   * Legacy connector editor path — redirects to `integrationConnector`
   */
  legacyIntegrationEditor: (id: string) => `/system-settings/integrations/${id}`,

  /**
   * Legacy bridge status path — redirects to `integrationBridge`
   */
  legacyBridgeStatus: (id: string) => `/system-settings/bridges/${id}`,

  /**
   * Old Integrations path, sub-paths included — redirects under `integrations`
   */
  legacyIntegrations: () => '/integrations',

  /**
   * Old Partners path, sub-paths included — redirects under `partners`
   */
  legacyPartners: () => '/partners',

  /**
   * Partners page path (manage suppliers / customers)
   */
  partners: () => '/settings/partners',

  /**
   * Party (company) detail page
   */
  partnerDetail: (id: string) => `/settings/partners/${id}`,

  /**
   * The bank accounts section of a party's detail page
   */
  partnerBankAccounts: (id: string) => `/settings/partners/${id}#${PARTNER_BANK_ACCOUNTS_ANCHOR}`,

  /**
   * New party form — the create route of the partners detail page
   */
  partnerCreate: () => '/settings/partners/new',

  /**
   * Billing success page — Stripe redirects here after successful checkout.
   */
  billingSuccess: () => '/billing/success',

  /**
   * Billing cancel page — Stripe redirects here when checkout is abandoned.
   */
  billingCancel: () => '/billing/cancel',

  /**
   * Where Stripe returns from a credit pack Checkout, with `?purchase=success|cancel`.
   */
  creditPurchaseReturn: () => '/billing/credits',

  /**
   * The account's credit statement
   */
  creditTransactions: () => '/billing/credit-transactions',

  /**
   * Legacy Terms of Service path — redirects to the Policies page (Terms tab).
   */
  termsOfService: () => '/terms-of-service',

  /**
   * Policies page (Terms, Privacy Policy, Subprocessors) — public, accessible without authentication.
   * @param tab - Optional tab to activate on load (`'terms'` | `'privacy'` | `'subprocessors'`)
   */
  policies: (tab?: string) => (tab ? `/policies?tab=${tab}` : '/policies')
} as const;

/**
 * Type-safe navigation helper to get all available paths
 */
export type NavigationPaths = ReturnType<(typeof navigation)[keyof typeof navigation]>;
